"use client";

import { useEffect, useState, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { useParams, useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ToolouerSection } from "@/components/ToolouerSection";
import { RentalDatePicker } from "@/components/ui/rental-date-picker";
import { MoreCategories } from "@/components/MoreCategories";
import { getProductImageUrl, formatPriceValue, toNumber, slugify } from "@/lib/utils";
import { useCart } from "@/lib/cart-context";
import { usePostalCode } from "@/lib/postal-code-context";
import { mapCartItemToGA4Item, mapProductToGA4Item, pushDataLayer } from "@/lib/gtm";
import { countRentalDays, isWeekendOnlyRental, computeRentalTotalHt, RENTAL_TIME_SLOTS } from "@/lib/rental-pricing";
import {
  ChevronLeft,
  ChevronRight,
  Truck,
  Shield,
  Phone,
  Calendar,
  MapPin,
  ShoppingCart,
  Plus,
  Minus,
  Check,
  Heart,
  Share2,
  Loader2,
  Home,
  Info,
} from "lucide-react";
import { QuoteRequestModal } from "@/components/QuoteRequestModal";

interface Product {
  id: number;
  title: string;
  slug: string;
  short_description: string | null;
  description: string | null;
  information: string | null;
  price_ht: number | null;
  display_rent_price_ht: number | null;
  display_rent_professional_price_ht: number | null;
  brand: string | null;
  sku: string | null;
  category_id: string | null;
  category_name: string | null;
  image_local: string | null;
  images_local: string | null;
  store_name: string | null;
  deposit_price: number | null;
  selling_type: string | null;
  logistic_class: string | null;
  weekend_price_ht: number | null;
  quote_only: boolean;
  free_weekends?: boolean;
  category_slug?: string | null;
}

interface ProductImage {
  local_path: string;
}

interface RentPrice {
  min_days: number;
  max_days: number;
  price_per_day: number | null;
  price_per_day_professional: number | null;
}

interface Carrier {
  delivery_method: string;
  min_days: number | null;
  max_days: number | null;
  available_after_days: number | null;
  price: number | null;
  free_threshold: number | null;
  allowed_zip_codes: string[] | null;
}

interface SiblingCategory {
  id: string;
  name: string;
  slug: string;
}

export default function ProductPage() {
  const params = useParams();
  const router = useRouter();
  const slug = params.slug as string;
  const { addToCart } = useCart();
  const { postalCode: savedPostalCode, hasPostalCode, isIDF } = usePostalCode();

  const [product, setProduct] = useState<Product | null>(null);
  const [images, setImages] = useState<ProductImage[]>([]);
  const [rentPrices, setRentPrices] = useState<RentPrice[]>([]);
  const [deliveryMethods, setDeliveryMethods] = useState<string[]>([]);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<string | null>(null);
  const [similarProducts, setSimilarProducts] = useState<Product[]>([]);
  const [siblingCategories, setSiblingCategories] = useState<SiblingCategory[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  // Cart-related state
  const [quantity, setQuantity] = useState(1);
  const [rentalStartDate, setRentalStartDate] = useState<string>("");
  const [rentalEndDate, setRentalEndDate] = useState<string>("");
  const [rentalStartTime, setRentalStartTime] = useState<string>("08:00");
  const [rentalEndTime, setRentalEndTime] = useState<string>("08:00");
  const [addedToCart, setAddedToCart] = useState(false);

  // Préremplit la période lorsqu'un produit est ouvert depuis le moteur de la home.
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const start = searchParams.get("rentalStartDate");
    const end = searchParams.get("rentalEndDate");
    if (start && /^\d{4}-\d{2}-\d{2}$/.test(start)) setRentalStartDate(start);
    if (end && /^\d{4}-\d{2}-\d{2}$/.test(end)) setRentalEndDate(end);
  }, [slug]);

  // Pro/Particulier toggle
  const [isProfessional, setIsProfessional] = useState(true);

  // Favorites state
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  // Quote modal
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  // Variations (sibling products with same parent_id)
  const [variations, setVariations] = useState<{
    id: number; title: string; slug: string;
    price_ht: string | null; display_rent_price_ht: string | null;
    image_local: string | null;
    attributes: { name: string; value: string }[];
  }[]>([]);

  const [closedDays, setClosedDays] = useState<number[]>([0]);

  // Shipping estimate
  const [shippingEstimate, setShippingEstimate] = useState<number | null>(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingClassName, setShippingClassName] = useState<string | null>(null);

  useEffect(() => {
    async function fetchProduct() {
      try {
        const res = await fetch(`/api/products/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setProduct(data.product);
          setImages(data.images || []);
          setRentPrices(data.rentPrices || []);
          const dm = data.deliveryMethods || [];
          setDeliveryMethods(dm);
          setCarriers(data.carriers || []);
          // Auto-select: AGENCY first (retrait magasin), then first available
          if (dm.includes("AGENCY")) {
            setSelectedDeliveryMethod("AGENCY");
          } else if (dm.length > 0) {
            setSelectedDeliveryMethod(dm[0]);
          }
          setSimilarProducts(data.similarProducts || []);
          setSiblingCategories(data.siblingCategories || []);
          setVariations(data.variations || []);
          setClosedDays(data.closedDays || [0]);


          // Check if product is in favorites
          if (data.product?.id) {
            fetch(`/api/favorites/${data.product.id}`)
              .then((r) => r.ok ? r.json() : null)
              .then((d) => { if (d) setIsFavorite(d.isFavorite); })
              .catch(() => {});
          }
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    }

    if (slug) {
      fetchProduct();
    }
  }, [slug]);

  const freeWeekends = product?.free_weekends ?? false;

  // GA4 view_item — déclenché une seule fois par produit, robuste à StrictMode/HMR
  const viewItemPushedFor = useRef<number | null>(null);
  useEffect(() => {
    if (!product) return;
    if (viewItemPushedFor.current === product.id) return;
    viewItemPushedFor.current = product.id;
    const priceHt = product.selling_type === "RENT"
      ? (toNumber(product.display_rent_price_ht) || 0)
      : (toNumber(product.price_ht) || 0);
    const item = mapProductToGA4Item({
      id: product.id,
      title: product.title,
      category_name: product.category_name,
      selling_type: product.selling_type,
      unit_price_ht: priceHt,
    });
    pushDataLayer("view_item", {
      currency: "EUR",
      value: priceHt,
      items: [item],
    });
  }, [product]);

  const handleStartDateChange = (value: string) => {
    setRentalStartDate(value);
    if (value && rentalEndDate && value > rentalEndDate) {
      setRentalEndDate(value);
    }
  };

  const handleEndDateChange = (value: string) => {
    setRentalEndDate(value);
  };

  // Calculate rental days and check weekend eligibility
  const selectedRentalDays = (() => {
    if (!rentalStartDate || !rentalEndDate) return 1;
    return countRentalDays(rentalStartDate, rentalEndDate, rentalStartTime, rentalEndTime, freeWeekends);
  })();

  // Weekend forfait only for particuliers, when rental is exactly Fri>=12h to Mon<=12h
  const weekendRental = !isProfessional && rentalStartDate && rentalEndDate
    ? isWeekendOnlyRental(rentalStartDate, rentalEndDate, rentalStartTime, rentalEndTime)
    : false;

  // Calculate shipping estimate
  useEffect(() => {
    if (!product?.logistic_class || !savedPostalCode || savedPostalCode.length < 2) {
      setShippingEstimate(null);
      setShippingClassName(null);
      return;
    }

    const abortController = new AbortController();
    setShippingLoading(true);

    fetch(
      `/api/shipping/calculate?logistic_class=${product.logistic_class}&postal_code=${savedPostalCode}`,
      { signal: abortController.signal }
    )
      .then((r) => r.json())
      .then((data) => {
        if (!abortController.signal.aborted) {
          if (data.available === false) {
            setShippingEstimate(null);
          } else {
            setShippingEstimate(data.total ?? null);
            setShippingClassName(data.className || null);
          }
        }
      })
      .catch(() => {
        if (!abortController.signal.aborted) {
          setShippingEstimate(null);
          setShippingClassName(null);
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) setShippingLoading(false);
      });

    return () => abortController.abort();
  }, [product?.logistic_class, savedPostalCode]);

  // No default dates — user must pick them explicitly

  const TVA_RATE = 0.20;

  // Whether user has selected both rental dates
  const hasRentalDates = !!(rentalStartDate && rentalEndDate);

  // Get the minimum price per day for "à partir de" display
  const getMinPricePerDayHt = (): number => {
    if (!product) return 0;
    const field = isProfessional ? "price_per_day_professional" : "price_per_day";
    const prices = rentPrices
      .map((rp) => Number(rp[field]))
      .filter((p) => p > 0);
    if (prices.length > 0) return Math.min(...prices);
    // Fallback to display fields
    if (isProfessional && product.display_rent_professional_price_ht != null) {
      return toNumber(product.display_rent_professional_price_ht) || 0;
    }
    return toNumber(product.display_rent_price_ht) || 0;
  };

  // Helper: find the right rent_prices row for a given number of days
  // Pro and particulier prices are on SEPARATE rows in the DB
  const findTierPrice = (days: number, professional: boolean): number | null => {
    for (const rp of rentPrices) {
      const maxDays = rp.max_days || Infinity;
      if (days >= rp.min_days && days <= maxDays) {
        if (professional && rp.price_per_day_professional != null) {
          return Number(rp.price_per_day_professional);
        }
        if (!professional && rp.price_per_day != null) {
          return Number(rp.price_per_day);
        }
      }
    }
    return null;
  };

  // Get price per day HT based on selected rental days and pro/particulier
  const getEffectivePricePerDayHt = (): number => {
    if (!product) return 0;
    if (product.selling_type === "RENT" && rentPrices.length > 0) {
      const price = findTierPrice(selectedRentalDays, isProfessional);
      if (price != null && price > 0) return price;
      // Fallback to display price
      if (isProfessional && product.display_rent_professional_price_ht != null) {
        return toNumber(product.display_rent_professional_price_ht) || 0;
      }
      return toNumber(product.display_rent_price_ht) || 0;
    }
    return toNumber(product.price_ht) || 0;
  };

  // Le forfait week-end correspond à 1,5 jour au tarif journalier applicable.
  const getWeekendPriceHt = (): number | null => {
    if (!product) return null;
    const dailyPrice = getEffectivePricePerDayHt();
    return dailyPrice > 0 ? dailyPrice * 1.5 : null;
  };

  // Format display price based on pro/particulier (pro = HT, particulier = TTC)
  const getDisplayPrice = (priceHt: number): number => {
    if (isProfessional) return priceHt;
    return priceHt * (1 + TVA_RATE);
  };

  const priceLabel = isProfessional ? "HT" : "TTC";

  // Get estimated delivery date for the selected delivery method
  const getEstimatedDate = (method: string): string | null => {
    const methodCarriers = carriers.filter((c) => c.delivery_method === method);
    if (methodCarriers.length === 0) return null;

    // Find the best matching carrier for user's postal code
    let carrier = methodCarriers[0];
    if (savedPostalCode && methodCarriers.length > 1) {
      const zipPrefix = savedPostalCode.slice(0, 2);
      const match = methodCarriers.find((c) =>
        c.allowed_zip_codes?.some((z) => savedPostalCode.startsWith(z) || z === zipPrefix)
      );
      if (match) carrier = match;
    }

    const daysToAdd = (carrier.available_after_days || 0) + (carrier.min_days || 0);
    const date = new Date();
    // Skip weekends
    let added = 0;
    while (added < daysToAdd) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    if (daysToAdd === 0) {
      return "Disponible immédiatement";
    }
    return `Dès le ${date.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "long" })}`;
  };

  // A product is "quote-only" when it has no direct price
  const isQuoteOnly = product
    ? product.quote_only || (product.selling_type === "RENT"
      ? !product.display_rent_price_ht && rentPrices.length === 0
      : !product.price_ht)
    : false;

  // Delivery method labels and icons config
  const deliveryMethodConfig: Record<string, { label: string; shortLabel: string }> = {
    AGENCY: { label: "Retrait en magasin", shortLabel: "Retrait" },
    DELIVERY: { label: "Livraison à domicile", shortLabel: "Domicile" },
    BUILDING_SITE: { label: "Livraison sur chantier", shortLabel: "Chantier" },
  };

  // Check if product is restricted to IDF (BUILDING_SITE only)
  const isBuildingSiteOnly = deliveryMethods.length > 0
    && deliveryMethods.includes("BUILDING_SITE")
    && !deliveryMethods.includes("DELIVERY")
    && !deliveryMethods.includes("AGENCY");

  const isUnavailable = isBuildingSiteOnly && (!hasPostalCode || !isIDF);

  const unavailableMessage = isBuildingSiteOnly
    ? (!hasPostalCode ? "Renseignez votre code postal" : "Indisponible dans votre région")
    : "";

  const handleAddToCart = () => {
    if (!product) return;

    const isRent = product.selling_type === "RENT";
    const priceHt = isRent ? getEffectivePricePerDayHt() : (toNumber(product.price_ht) || 0);

    const cartItem = {
      productId: product.id,
      productTitle: product.title,
      productSlug: product.slug,
      productImage: product.image_local,
      storeName: product.store_name,
      categoryName: product.category_name,
      sellingType: (isRent ? "RENT" : "NEW") as "RENT" | "NEW",
      quantity,
      unitPriceHt: priceHt,
      rentalDays: isRent ? selectedRentalDays : null,
      rentalStartDate: isRent ? rentalStartDate : null,
      rentalStartTime: isRent ? rentalStartTime : null,
      rentalEndDate: isRent ? rentalEndDate : null,
      rentalEndTime: isRent ? rentalEndTime : null,
      weekendPriceHt: isRent ? (getWeekendPriceHt() ?? null) : null,
      isWeekendRental: isRent ? weekendRental : false,
      depositPrice: isRent ? (toNumber(product.deposit_price) || null) : null,
      deliveryMethod: selectedDeliveryMethod,
      freeWeekends: isRent ? freeWeekends : false,
    };

    addToCart(cartItem);

    // GA4 add_to_cart
    const ga4Item = mapCartItemToGA4Item(cartItem);
    pushDataLayer("add_to_cart", {
      currency: "EUR",
      value: Math.round(ga4Item.price * ga4Item.quantity * 100) / 100,
      items: [ga4Item],
    });

    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const toggleFavorite = async () => {
    if (!product) return;
    setFavoriteLoading(true);
    try {
      if (isFavorite) {
        await fetch(`/api/favorites/${product.id}`, { method: "DELETE" });
        setIsFavorite(false);
      } else {
        const res = await fetch("/api/favorites", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ productId: product.id }),
        });
        if (res.ok) {
          setIsFavorite(true);
        } else if (res.status === 401) {
          window.location.href = "/connexion";
          return;
        }
      }
    } catch {
      // ignore
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleShare = async () => {
    if (!product) return;
    const url = `${window.location.origin}/produits/${product.slug}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: product.title, url });
      } catch {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(url);
      alert("Lien copié dans le presse-papier !");
    }
  };

  if (loading) {
    return (
      <main className="py-8 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="aspect-square bg-gray-200 rounded-xl"></div>
              <div className="space-y-4">
                <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-12 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="py-8 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 text-center py-12">
          <h1 className="text-2xl font-bold mb-4">Produit non trouvé</h1>
          <p className="text-gray-500 mb-6">
            Ce produit n&apos;existe pas ou a été supprimé.
          </p>
          <Button asChild>
            <Link href="/categories">Voir les catégories</Link>
          </Button>
        </div>
      </main>
    );
  }

  // Parser les images locales du JSON
  let allImages: string[] = [];
  if (product.images_local) {
    try {
      allImages = JSON.parse(product.images_local);
    } catch {
      if (product.image_local) {
        allImages = [product.image_local];
      }
    }
  } else if (product.image_local) {
    allImages = [product.image_local];
  }

  // Utiliser les images de la table product_images si disponibles
  if (images.length > 0) {
    allImages = images.map((img) => img.local_path);
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex(
      (prev) => (prev - 1 + allImages.length) % allImages.length
    );
  };

  return (
    <main className="py-8 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="text-sm mb-6">
          <ol className="flex items-center gap-2 text-gray-500">
            <li>
              <Link href="/" className="hover:text-secondary">
                <Home className="h-4 w-4" />
              </Link>
            </li>
            {product.category_name && (
              <>
                <li>/</li>
                <li>
                  <Link href={`/categories/${product.category_slug || slugify(product.category_name || '')}`} className="hover:text-secondary">
                    {product.category_name}
                  </Link>
                </li>
              </>
            )}
            <li>/</li>
            <li className="text-black font-medium truncate max-w-[200px]">
              {product.title}
            </li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Images */}
          <div>
            <div className="relative -mx-4 mb-4 aspect-square overflow-hidden bg-white md:mx-0 md:rounded-xl">
              {allImages.length > 0 ? (
                <>
                  <Image
                    src={getProductImageUrl(allImages[currentImageIndex])}
                    alt={product.title}
                    fill
                    className="object-contain p-8"
                    priority
                  />
                  {allImages.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full shadow hover:bg-white"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full shadow hover:bg-white"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  Pas d&apos;image
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {allImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {allImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`relative w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden border-2 ${
                      index === currentImageIndex
                        ? "border-secondary"
                        : "border-transparent"
                    }`}
                  >
                    <Image
                      src={getProductImageUrl(img)}
                      alt={`${product.title} - ${index + 1}`}
                      fill
                      className="object-contain p-1"
                    />
                  </button>
                ))}
              </div>
            )}

            {product.short_description && (
              <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5 shadow-sm sm:mt-4 sm:gap-3 sm:rounded-xl sm:px-4 sm:py-3.5">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-600 sm:h-7 sm:w-7">
                  <Info className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
                </span>
                <div>
                  <p className="mb-0.5 text-[10px] font-bold uppercase tracking-wide text-orange-600 sm:text-xs">
                    À savoir
                  </p>
                  <p className="text-xs font-medium leading-relaxed text-gray-800 sm:text-sm">
                    {product.short_description}
                  </p>
                </div>
              </div>
            )}

            {/* Favorite + Share buttons */}
            <div className="flex items-center justify-end gap-2 mt-2">
              <button
                onClick={handleShare}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <Share2 className="h-4 w-4" />
                <span>Partager</span>
              </button>
              <button
                onClick={toggleFavorite}
                disabled={favoriteLoading}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-colors ${
                  isFavorite
                    ? "border-red-200 bg-red-50 text-red-600"
                    : "border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                <Heart className={`h-4 w-4 ${isFavorite ? "fill-red-500 text-red-500" : ""}`} />
                <span>{isFavorite ? "Favori" : "Ajouter aux favoris"}</span>
              </button>
            </div>
          </div>

          {/* Infos produit */}
          <div>
            <div className="-mx-4 mb-6 bg-white p-6 md:mx-0 md:rounded-xl">
              <p className="text-sm text-gray-500 mb-2">
                {product.brand || "TOOLOUER"}
              </p>
              <h1 className="text-2xl font-bold mb-4">{product.title}</h1>

              {/* Variations selector */}
              {variations.length > 1 && (() => {
                // Find which attributes differ between variations (these are the selectors)
                const allAttrNames: string[] = [];
                const seen = new Set<string>();
                variations.forEach(v => v.attributes.forEach(a => {
                  if (!seen.has(a.name)) { seen.add(a.name); allAttrNames.push(a.name); }
                }));
                const varyingAttrs = allAttrNames.filter(name => {
                  const values = new Set(variations.map(v => v.attributes.find(a => a.name === name)?.value).filter(Boolean));
                  return values.size > 1;
                });

                if (varyingAttrs.length === 0) {
                  // No distinguishing attributes — show by title
                  // ≤5: buttons, >5: dropdown
                  if (variations.length <= 5) {
                    return (
                      <div className="mb-6">
                        <p className="text-sm font-medium text-gray-700 mb-2">Variantes disponibles</p>
                        <div className="flex flex-wrap gap-2">
                          {variations.map(v => (
                            <Link
                              key={v.id}
                              href={`/produits/${v.slug}`}
                              className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                                v.id === product.id
                                  ? "border-secondary bg-secondary/5 text-secondary font-medium"
                                  : "border-gray-200 hover:border-secondary/50 text-gray-700"
                              }`}
                            >
                              {v.title}
                            </Link>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className="mb-6">
                      <p className="text-sm font-medium text-gray-700 mb-2">Variantes disponibles</p>
                      <select
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-secondary/50 focus:border-secondary"
                        value={product.id}
                        onChange={(e) => {
                          const v = variations.find(v => v.id === Number(e.target.value));
                          if (v) router.push(`/produits/${v.slug}`);
                        }}
                      >
                        {variations.map(v => (
                          <option key={v.id} value={v.id}>{v.title}</option>
                        ))}
                      </select>
                    </div>
                  );
                }

                // Current product's attribute values
                const currentAttrs = new Map<string, string>();
                const currentVar = variations.find(v => v.id === product.id);
                currentVar?.attributes.forEach(a => { if (a.value) currentAttrs.set(a.name, a.value); });

                // For each varying attribute, get unique values
                const attrOptions = varyingAttrs.map(name => {
                  const uniqueVals: string[] = [];
                  const valSeen = new Set<string>();
                  variations.forEach(v => {
                    const val = v.attributes.find(a => a.name === name)?.value;
                    if (val && !valSeen.has(val)) { valSeen.add(val); uniqueVals.push(val); }
                  });
                  return { name, values: uniqueVals };
                });

                // Exact match : vérifie si la combinaison exacte courante + valeur changée existe.
                // Sert à marquer un bouton disponible/indisponible.
                const findExactVariation = (changedAttr: string, changedValue: string) => {
                  const desired = new Map(currentAttrs);
                  desired.set(changedAttr, changedValue);
                  return variations.find(v =>
                    varyingAttrs.every(name => {
                      const vVal = v.attributes.find(a => a.name === name)?.value;
                      return vVal === desired.get(name);
                    })
                  );
                };

                // Pour le clic : exact match si possible, sinon meilleur fallback
                // (variation qui matche le maximum d'attributs courants).
                const findBestVariation = (changedAttr: string, changedValue: string) => {
                  const exact = findExactVariation(changedAttr, changedValue);
                  if (exact) return exact;
                  // Fallback : variations qui ont la valeur cliquée, classées par nombre d'attributs matchés
                  const candidates = variations
                    .filter(v => v.attributes.find(a => a.name === changedAttr)?.value === changedValue)
                    .map(v => {
                      const matches = varyingAttrs.filter(name =>
                        v.attributes.find(a => a.name === name)?.value === currentAttrs.get(name)
                      ).length;
                      return { v, matches };
                    })
                    .sort((a, b) => b.matches - a.matches);
                  return candidates[0]?.v;
                };


                return (
                  <div className="mb-6 space-y-4">
                    {attrOptions.map(({ name, values }) => (
                      <div key={name}>
                        <p className="text-sm font-medium text-gray-700 mb-2">{name}</p>
                        {values.length <= 5 ? (
                          <div className="flex flex-wrap gap-2">
                            {values.map(val => {
                              const isSelected = currentAttrs.get(name) === val;
                              // exact match avec les autres attributs courants ?
                              const exactTarget = findExactVariation(name, val);
                              const isAvailable = !!exactTarget;
                              // Toujours navigable (le clic prend la "best" variation)
                              const navTarget = findBestVariation(name, val);

                              if (isSelected) {
                                return (
                                  <span
                                    key={val}
                                    className="px-3 py-2 text-sm rounded-lg border-2 border-secondary bg-secondary/5 text-secondary font-medium"
                                  >
                                    {val}
                                  </span>
                                );
                              }

                              if (!isAvailable) {
                                // Indisponible avec la combinaison actuelle, mais cliquable :
                                // ça redirige vers la "best" variation possible avec cette valeur
                                if (!navTarget) {
                                  return (
                                    <span
                                      key={val}
                                      title="Combinaison non disponible"
                                      className="px-3 py-2 text-sm rounded-lg border border-gray-100 text-gray-300 line-through cursor-not-allowed"
                                    >
                                      {val}
                                    </span>
                                  );
                                }
                                return (
                                  <Link
                                    key={val}
                                    href={`/produits/${navTarget.slug}`}
                                    title="Indisponible avec votre sélection actuelle. Cliquez pour ajuster les autres options."
                                    className="px-3 py-2 text-sm rounded-lg border border-dashed border-gray-300 text-gray-400 line-through hover:border-secondary/50 hover:text-gray-600 transition-colors"
                                  >
                                    {val}
                                  </Link>
                                );
                              }

                              return (
                                <Link
                                  key={val}
                                  href={`/produits/${exactTarget.slug}`}
                                  className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:border-secondary/50 text-gray-700 transition-colors"
                                >
                                  {val}
                                </Link>
                              );
                            })}
                          </div>
                        ) : (
                          <select
                            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-secondary/50 focus:border-secondary"
                            value={currentAttrs.get(name) || ""}
                            onChange={(e) => {
                              const target = findBestVariation(name, e.target.value);
                              if (target) router.push(`/produits/${target.slug}`);
                            }}
                          >
                            {values.map(val => {
                              const exact = findExactVariation(name, val);
                              const label = exact ? val : `${val} (combinaison indispo)`;
                              return (
                                <option key={val} value={val}>{label}</option>
                              );
                            })}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Pro / Particulier Toggle */}
              {!isQuoteOnly && (product.selling_type === "RENT" || product.selling_type === "NEW") && (
                <div className="mb-6">
                  <div className="flex items-center">
                    <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                      <button
                        onClick={() => setIsProfessional(true)}
                        className={`px-5 py-2 text-sm font-medium transition-colors ${
                          isProfessional
                            ? "bg-secondary text-white"
                            : "bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        Professionnel
                      </button>
                      <button
                        onClick={() => setIsProfessional(false)}
                        className={`px-5 py-2 text-sm font-medium transition-colors ${
                          !isProfessional
                            ? "bg-secondary text-white"
                            : "bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        Particulier
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Tarifs dégressifs - badges indicatifs (seulement si plusieurs paliers) */}
              {(() => {
                if (isQuoteOnly) return null;
                // Filter tiers relevant to the current mode (pro or part.)
                const relevantTiers = rentPrices.filter((rp) =>
                  isProfessional ? rp.price_per_day_professional != null : rp.price_per_day != null
                );
                if (relevantTiers.length <= 1) return null;
                return (
                  <div className="mb-6">
                    <p className="text-sm font-semibold text-gray-700 mb-3">Tarifs dégressifs</p>
                    <div className="flex flex-wrap gap-2">
                      {relevantTiers.map((rp, index) => {
                        const priceHt = isProfessional
                          ? Number(rp.price_per_day_professional)
                          : Number(rp.price_per_day);
                        const displayPrice = getDisplayPrice(priceHt);
                        const maxDays = rp.max_days || null;
                        return (
                          <div
                            key={index}
                            className="flex flex-col items-center px-4 py-2 border border-gray-200 rounded-lg bg-white"
                          >
                            <span className="text-sm font-bold text-secondary">
                              {formatPriceValue(displayPrice)}€ /{priceLabel}
                            </span>
                            <span className="text-xs text-gray-500 uppercase tracking-wide">
                              {maxDays
                                ? rp.min_days === maxDays
                                  ? `${rp.min_days} jour${rp.min_days > 1 ? "s" : ""}`
                                  : `${rp.min_days} à ${maxDays} jours`
                                : `${rp.min_days}+ jours`}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Prix principal */}
              <div className="mb-6">
                {isQuoteOnly ? (
                  <p className="text-xl text-gray-600">Prix sur devis</p>
                ) : product.display_rent_price_ht || rentPrices.length > 0 ? (
                  <div>
                    <div className="flex items-baseline gap-2">
                      {hasRentalDates ? (
                        <>
                          <span className="text-3xl font-bold text-secondary whitespace-nowrap">
                            {formatPriceValue(getDisplayPrice(getEffectivePricePerDayHt()))}&nbsp;€
                          </span>
                          <span className="text-gray-500">{priceLabel} / jour</span>
                        </>
                      ) : (
                        <>
                          <span className="text-sm text-gray-500">À partir de</span>
                          <span className="text-3xl font-bold text-secondary whitespace-nowrap">
                            {formatPriceValue(getDisplayPrice(getMinPricePerDayHt()))}&nbsp;€
                          </span>
                          <span className="text-gray-500">{priceLabel} / jour</span>
                        </>
                      )}
                    </div>
                    {product.deposit_price && (
                      <p className="text-sm text-gray-500 mt-1">
                        Caution : {formatPriceValue(getDisplayPrice(toNumber(product.deposit_price) || 0))} € {priceLabel}
                      </p>
                    )}
                  </div>
                ) : product.price_ht ? (
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-secondary whitespace-nowrap">
                      {formatPriceValue(getDisplayPrice(toNumber(product.price_ht) || 0))}&nbsp;€
                    </span>
                    <span className="text-gray-500">{priceLabel}</span>
                  </div>
                ) : (
                  <p className="text-xl text-gray-600">Prix sur devis</p>
                )}
              </div>

              {/* Toggle Mode de livraison — masqué si produit devis-only */}
              {deliveryMethods.length > 0 && !isQuoteOnly && (
                <div className="mb-6">
                  <span className="text-sm font-semibold text-gray-700 block mb-2">Mode de livraison</span>
                  {deliveryMethods.length === 1 ? (
                    // Single method — just display it
                    <div className="flex items-center gap-3 p-3 rounded-lg border-2 border-secondary bg-secondary/5">
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                        {deliveryMethods[0] === "AGENCY" && (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" /><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" /><path d="M2 7h20" /><path d="M22 7v3a2 2 0 0 1-2 2a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7" />
                          </svg>
                        )}
                        {deliveryMethods[0] === "DELIVERY" && (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" /><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                          </svg>
                        )}
                        {deliveryMethods[0] === "BUILDING_SITE" && (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" /><path d="M15 18H9" /><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" /><circle cx="17" cy="18" r="2" /><circle cx="7" cy="18" r="2" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <span className="text-sm font-medium text-secondary">
                          {deliveryMethodConfig[deliveryMethods[0]]?.label || deliveryMethods[0]}
                        </span>
                        {(() => {
                          const est = getEstimatedDate(deliveryMethods[0]);
                          return est ? (
                            <p className="text-xs text-gray-500 mt-0.5">{est}</p>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  ) : (
                    // Multiple methods — toggle
                    <div className="flex flex-col gap-2">
                      {deliveryMethods
                        .filter((m) => m !== "BUILDING_SITE" || isIDF)
                        .map((method) => {
                          const isSelected = selectedDeliveryMethod === method;
                          const est = getEstimatedDate(method);
                          return (
                            <button
                              key={method}
                              onClick={() => setSelectedDeliveryMethod(method)}
                              className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                                isSelected
                                  ? "border-secondary bg-secondary/5"
                                  : "border-gray-200 bg-white hover:border-gray-300"
                              }`}
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                isSelected ? "bg-secondary" : "bg-gray-300"
                              }`}>
                                {method === "AGENCY" && (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" /><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" /><path d="M2 7h20" /><path d="M22 7v3a2 2 0 0 1-2 2a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7" />
                                  </svg>
                                )}
                                {method === "DELIVERY" && (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" /><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                  </svg>
                                )}
                                {method === "BUILDING_SITE" && (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" /><path d="M15 18H9" /><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" /><circle cx="17" cy="18" r="2" /><circle cx="7" cy="18" r="2" />
                                  </svg>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className={`text-sm font-medium ${isSelected ? "text-secondary" : "text-gray-700"}`}>
                                  {deliveryMethodConfig[method]?.label || method}
                                </span>
                                {est && (
                                  <p className={`text-xs mt-0.5 ${isSelected ? "text-secondary/70" : "text-gray-400"}`}>
                                    {est}
                                  </p>
                                )}
                              </div>
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                isSelected ? "border-secondary" : "border-gray-300"
                              }`}>
                                {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-secondary" />}
                              </div>
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="space-y-3">
                {/* Type Location */}
                {product.selling_type === "RENT" ? (
                  <>
                    {!isQuoteOnly && (product.display_rent_price_ht || rentPrices.length > 0) ? (
                      <div className="space-y-4">
                        {/* Date et heure de début de location */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Date et heure de début
                          </label>
                          <div className="flex gap-2">
                            <RentalDatePicker
                              value={rentalStartDate}
                              onChange={handleStartDateChange}
                              min={new Date().toISOString().split("T")[0]}
                              closedDays={closedDays}
                              placeholder="Date de début"
                              className="flex-1"
                            />
                            <select
                              value={rentalStartTime}
                              onChange={(e) => setRentalStartTime(e.target.value)}
                              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                            >
                              {RENTAL_TIME_SLOTS.map((t) => (
                                <option key={t} value={t}>{t.replace(":", "h")}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Date et heure de fin de location */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Date et heure de fin
                          </label>
                          <div className="flex gap-2">
                            <RentalDatePicker
                              value={rentalEndDate}
                              onChange={handleEndDateChange}
                              min={rentalStartDate || new Date().toISOString().split("T")[0]}
                              closedDays={closedDays}
                              placeholder="Date de fin"
                              className="flex-1"
                            />
                            <select
                              value={rentalEndTime}
                              onChange={(e) => setRentalEndTime(e.target.value)}
                              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                            >
                              {RENTAL_TIME_SLOTS.map((t) => (
                                <option key={t} value={t}>{t.replace(":", "h")}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Free weekends info */}
                        {freeWeekends && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                            <p className="font-medium">Week-ends non facturés</p>
                            <p className="text-xs text-green-600 mt-1">
                              Les samedis et dimanches ne sont pas comptés dans la durée de location
                            </p>
                          </div>
                        )}

                        {/* Weekend forfait info */}
                        {!freeWeekends && hasRentalDates && weekendRental && getWeekendPriceHt() != null && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                            <p className="font-medium">Tarif week-end appliqué</p>
                            <p className="text-xs text-blue-600 mt-1">
                              Forfait week-end : du vendredi après 12h au lundi avant 12h
                            </p>
                          </div>
                        )}

                        {/* Quantité */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Quantité :
                          </label>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                              disabled={quantity <= 1}
                              className="w-8 h-8 rounded-full bg-secondary text-white flex items-center justify-center text-lg font-bold disabled:opacity-40 hover:bg-secondary/90 transition-colors"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="text-lg font-semibold w-8 text-center">{quantity}</span>
                            <button
                              onClick={() => setQuantity((q) => q + 1)}
                              className="w-8 h-8 rounded-full bg-secondary text-white flex items-center justify-center text-lg font-bold hover:bg-secondary/90 transition-colors"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {/* Total estimate — only when dates are selected */}
                        {hasRentalDates && (
                          <div className="bg-gray-50 p-3 rounded-lg space-y-1">
                            {weekendRental && getWeekendPriceHt() != null ? (
                              <>
                                {/* Weekend forfait line */}
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">
                                    Forfait week-end{quantity > 1 ? ` x ${quantity}` : ""}
                                  </span>
                                  <span className="font-semibold">
                                    {formatPriceValue(getDisplayPrice(getWeekendPriceHt()!) * quantity)} € {priceLabel}
                                  </span>
                                </div>
                              </>
                            ) : (
                              <>
                                {/* Standard per-day line */}
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">
                                    {formatPriceValue(getDisplayPrice(getEffectivePricePerDayHt()))} € x {selectedRentalDays} jour{selectedRentalDays > 1 ? "s" : ""}{quantity > 1 ? ` x ${quantity}` : ""}
                                  </span>
                                  <span className="font-semibold">
                                    {formatPriceValue(getDisplayPrice(getEffectivePricePerDayHt()) * selectedRentalDays * quantity)} € {priceLabel}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        )}

                        {isUnavailable ? (
                          <Button
                            className="w-full bg-gray-300 text-gray-500 py-6 text-lg cursor-not-allowed"
                            disabled
                          >
                            <MapPin className="mr-2 h-5 w-5" />
                            {unavailableMessage}
                          </Button>
                        ) : !hasRentalDates ? (
                          <Button
                            className="w-full bg-gray-300 text-gray-500 py-6 text-lg cursor-not-allowed"
                            disabled
                          >
                            <ShoppingCart className="mr-2 h-5 w-5" />
                            Sélectionnez vos dates
                          </Button>
                        ) : (
                          <Button
                            className="w-full bg-secondary hover:bg-secondary/90 text-white py-6 text-lg"
                            onClick={handleAddToCart}
                            disabled={addedToCart}
                          >
                            {addedToCart ? (
                              <>
                                <Check className="mr-2 h-5 w-5" />
                                Ajouté au panier
                              </>
                            ) : (
                              <>
                                <ShoppingCart className="mr-2 h-5 w-5" />
                                Ajouter au panier
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    ) : (
                      <Button className="w-full bg-secondary hover:bg-secondary/90 text-white py-6 text-lg" onClick={() => setQuoteModalOpen(true)}>
                        <Phone className="mr-2 h-5 w-5" />
                        Demander un devis
                      </Button>
                    )}
                  </>
                ) : (
                  /* Type Achat */
                  <>
                    {!isQuoteOnly && product.price_ht ? (
                      <div className="space-y-4">
                        {/* Quantity selector */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quantité
                          </label>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-10 w-10"
                              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                              disabled={quantity <= 1}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <Input
                              type="number"
                              min={1}
                              value={quantity}
                              onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                              className="w-20 text-center"
                            />
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-10 w-10"
                              onClick={() => setQuantity((q) => q + 1)}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {isUnavailable ? (
                          <Button
                            className="w-full bg-gray-300 text-gray-500 py-6 text-lg cursor-not-allowed"
                            disabled
                          >
                            <MapPin className="mr-2 h-5 w-5" />
                            {unavailableMessage}
                          </Button>
                        ) : (
                          <Button
                            className="w-full bg-secondary hover:bg-secondary/90 text-white py-6 text-lg"
                            onClick={handleAddToCart}
                            disabled={addedToCart}
                          >
                            {addedToCart ? (
                              <>
                                <Check className="mr-2 h-5 w-5" />
                                Ajouté au panier
                              </>
                            ) : (
                              <>
                                <ShoppingCart className="mr-2 h-5 w-5" />
                                Ajouter au panier
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    ) : (
                      <Button className="w-full bg-secondary hover:bg-secondary/90 text-white py-6 text-lg" onClick={() => setQuoteModalOpen(true)}>
                        <Phone className="mr-2 h-5 w-5" />
                        Demander un devis
                      </Button>
                    )}
                  </>
                )}
              </div>

              {/* Estimation frais de livraison — uniquement pour les modes avec transport et si le produit a un prix */}
              {!isQuoteOnly && selectedDeliveryMethod && selectedDeliveryMethod !== "AGENCY" && hasPostalCode && savedPostalCode && savedPostalCode.length >= 2 && (product.price_ht || product.display_rent_price_ht || rentPrices.length > 0) && (
                <div className="mt-3 px-3 py-2.5 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2 text-sm">
                    <Truck className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    {shippingLoading ? (
                      <span className="text-blue-600 flex items-center gap-1.5">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Calcul des frais...
                      </span>
                    ) : shippingEstimate !== null ? (
                      <span className="text-blue-700">
                        Livraison estimée :{" "}
                        <span className="font-semibold">
                          {shippingEstimate === 0 ? "Gratuit" : `${formatPriceValue(shippingEstimate / (1 + TVA_RATE))} € HT`}
                        </span>
                        <span className="text-blue-500 ml-1">
                          ({savedPostalCode})
                        </span>
                      </span>
                    ) : (
                      <span className="text-blue-500">
                        Frais de livraison non disponibles pour {savedPostalCode}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Paiement sécurisé - uniquement si achat avec prix */}
              {!isQuoteOnly && product.selling_type !== "RENT" && product.price_ht && (
                <div className="mt-4 pt-4 border-t text-center">
                  <p className="text-sm font-medium text-gray-600 mb-2">
                    Paiement en ligne sécurisé
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <Image
                      src="/assets/Visa.png"
                      alt="Visa"
                      width={36}
                      height={23}
                      className="object-contain"
                    />
                    <Image
                      src="/assets/master-card.png"
                      alt="Mastercard"
                      width={36}
                      height={23}
                      className="object-contain"
                    />
                    <Image
                      src="/assets/CB.png"
                      alt="CB"
                      width={36}
                      height={23}
                      className="object-contain"
                    />
                    <Image
                      src="/assets/apple-pay.png"
                      alt="Apple Pay"
                      width={36}
                      height={23}
                      className="object-contain"
                    />
                    <Image
                      src="/assets/Paypal.png"
                      alt="PayPal"
                      width={36}
                      height={23}
                      className="object-contain"
                    />
                  </div>
                </div>
              )}

              {/* Numéro du service client */}
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-9 h-9 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="h-4 w-4 text-secondary" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Service client</p>
                    <a
                      href="tel:+33189293489"
                      className="text-sm font-semibold text-secondary hover:underline"
                    >
                      01 89 29 34 89
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Bon à savoir - Reassurance */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <p className="font-semibold text-sm mb-3">Bon à savoir :</p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Image
                    src="/assets/certified-seller.png"
                    alt="Vendeur certifié"
                    width={24}
                    height={24}
                  />
                  <span className="text-sm text-gray-600">Vendeur certifié</span>
                </div>
                <div className="flex items-center gap-2">
                  <Image
                    src="/assets/fast-delivery.png"
                    alt="Livraison rapide"
                    width={24}
                    height={24}
                  />
                  <span className="text-sm text-gray-600">Livraison rapide</span>
                </div>
                <div className="flex items-center gap-2">
                  <Image
                    src="/assets/helmet.png"
                    alt="Satisfaction garantie"
                    width={24}
                    height={19}
                  />
                  <span className="text-sm text-gray-600">Satisfaction garantie</span>
                </div>
              </div>
            </div>

            {/* Avantages - masqué pour le moment */}
            {/* <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 text-center">
                <Truck className="h-8 w-8 mx-auto mb-2 text-secondary" />
                <p className="text-sm font-medium">Livraison possible</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <Shield className="h-8 w-8 mx-auto mb-2 text-secondary" />
                <p className="text-sm font-medium">Matériel assuré</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-2 text-secondary" />
                <p className="text-sm font-medium">Retrait en agence</p>
              </div>
            </div> */}
          </div>
        </div>

        {/* Description */}
        {(product.description || product.information) && (
          <div className="-mx-4 mt-8 bg-white p-6 md:mx-0 md:rounded-xl">
            <h2 className="text-xl font-bold mb-4">Description</h2>
            <div className="prose prose-gray max-w-none">
              {product.description && (
                <div dangerouslySetInnerHTML={{ __html: product.description }} />
              )}
              {product.information && (
                <div className="mt-4">
                  <h3 className="font-semibold text-black mb-2">
                    Informations techniques
                  </h3>
                  <div dangerouslySetInnerHTML={{ __html: product.information }} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Produits similaires */}
        {similarProducts.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold mb-6">Produits similaires</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {similarProducts.map((p) => (
                <Link
                  key={p.id}
                  href={`/produits/${p.slug}`}
                  className="bg-white rounded-xl border hover:shadow-lg transition-shadow group overflow-hidden"
                >
                  <div className="aspect-square relative bg-white">
                    <Image
                      src={getProductImageUrl(p.image_local)}
                      alt={p.title}
                      fill
                      className="object-contain p-4"
                    />
                  </div>
                  <div className="p-4 bg-gray-50">
                    <h3 className="font-medium text-sm line-clamp-2 group-hover:text-secondary">
                      {p.title}
                    </h3>
                    {p.display_rent_price_ht && (
                      <p className="text-secondary font-bold mt-2">
                        {formatPriceValue(p.display_rent_price_ht)} € HT/jour
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Section Toolouer */}
      <ToolouerSection />

      {/* Plus de catégories */}
      {siblingCategories.length > 0 && (
        <MoreCategories
          categories={siblingCategories}
          currentCategoryId={product.category_id || undefined}
        />
      )}

      {/* Quote request modal */}
      <QuoteRequestModal
        isOpen={quoteModalOpen}
        onClose={() => setQuoteModalOpen(false)}
        product={{
          id: product.id,
          title: product.title,
          slug: product.slug,
          store_name: product.store_name,
          image_local: product.image_local,
          sku: product.sku ?? null,
        }}
      />
    </main>
  );
}
